import { useLayoutEffect, useRef } from 'react';
import {
	Button,
	Calendar,
	CalendarCell,
	CalendarGrid,
	CalendarGridBody,
	CalendarGridHeader,
	CalendarHeaderCell,
	Heading,
} from 'react-aria-components';
import { CalendarDateTime, getLocalTimeZone, today } from '@internationalized/date';
import { ChevronLeft, ChevronRight, Clock } from 'lucide-react';
import { cn } from '../../utils';

/**
 * Shared, DS-styled building blocks for the react-aria date/time pickers.
 *
 * These mirror the prototype in
 * `ds-datepicker-prototype/src/approaches/ReactAria.tsx`, restyled against the
 * real 9ID design tokens (primary `var(--color-primary)`, zinc, Outfit,
 * rounded-2xl/rounded-xl, soft/glow shadows, focus ring primary/50).
 */

/** Light vs. dark styling for the react-aria date/time pickers. */
export type DsDateTheme = 'light' | 'dark';

/** Per-segment styling for the segmented date/time field. */
export function segmentClass(theme: DsDateTheme = 'light') {
	return theme === 'dark'
		? 'px-0.5 tabular-nums outline-none rounded ' +
				'data-[placeholder]:text-zinc-500 ' +
				'focus:bg-primary focus:text-zinc-900 ' +
				'data-[type=literal]:text-zinc-500 data-[type=literal]:px-0'
		: 'px-0.5 tabular-nums outline-none rounded ' +
				'data-[placeholder]:text-zinc-400 ' +
				'focus:bg-primary focus:text-zinc-900 ' +
				'data-[type=literal]:text-zinc-400 data-[type=literal]:px-0';
}

/** The field shell, matching the production trigger idiom. */
export function fieldGroupClass(invalid: boolean, className?: string, theme: DsDateTheme = 'light') {
	return theme === 'dark'
		? cn(
				'bg-zinc-800 border rounded-lg text-sm transition-all py-1.5 px-2.5',
				'flex items-center gap-2 w-full',
				'focus-within:ring-2 focus-within:ring-primary/50',
				invalid ? 'border-error-500' : 'border-zinc-600',
				className,
			)
		: cn(
				'bg-zinc-50 border rounded-2xl text-sm transition-all py-3.5 px-4',
				'flex items-center gap-2 w-full',
				'focus-within:ring-2 focus-within:ring-primary/50',
				invalid ? 'border-error-500' : 'border-transparent',
				className,
			);
}

/** Popover surface for the calendar / slot list. */
export function popupClass(theme: DsDateTheme = 'light') {
	return theme === 'dark'
		? 'bg-zinc-800 rounded-2xl shadow-lg border border-zinc-700 p-4 z-[200] ' +
				'origin-top-left data-[entering]:opacity-0 data-[entering]:scale-95 ' +
				'transition-all duration-150'
		: 'bg-white rounded-2xl shadow-soft border border-zinc-200 p-4 z-[200] ' +
				'origin-top-left data-[entering]:opacity-0 data-[entering]:scale-95 ' +
				'transition-all duration-150';
}

/** A single day cell in the calendar grid. */
export function dayCellClass(theme: DsDateTheme = 'light') {
	return theme === 'dark'
		? 'w-8 h-8 flex items-center justify-center rounded-xl text-sm font-medium outline-none ' +
				'text-zinc-200 transition-colors cursor-default ' +
				'hover:bg-zinc-700 ' +
				'data-[outside-month]:text-zinc-600 ' +
				'data-[disabled]:text-zinc-600 data-[disabled]:hover:bg-transparent ' +
				'data-[unavailable]:text-zinc-600 data-[unavailable]:line-through data-[unavailable]:hover:bg-transparent ' +
				'data-[today]:bg-zinc-700 ' +
				'data-[selected]:bg-primary data-[selected]:text-zinc-900 data-[selected]:hover:bg-primary ' +
				'data-[selection-start]:bg-primary data-[selection-start]:text-zinc-900 ' +
				'data-[selection-end]:bg-primary data-[selection-end]:text-zinc-900 ' +
				'focus-visible:ring-2 focus-visible:ring-primary/50'
		: 'w-8 h-8 flex items-center justify-center rounded-xl text-sm font-medium outline-none ' +
				'text-zinc-700 transition-colors cursor-default ' +
				'hover:bg-zinc-100 ' +
				'data-[outside-month]:text-zinc-300 ' +
				'data-[disabled]:text-zinc-300 data-[disabled]:hover:bg-transparent ' +
				'data-[unavailable]:text-zinc-300 data-[unavailable]:line-through data-[unavailable]:hover:bg-transparent ' +
				'data-[today]:bg-zinc-100 ' +
				'data-[selected]:bg-primary data-[selected]:text-zinc-900 data-[selected]:hover:bg-primary ' +
				'data-[selection-start]:bg-primary data-[selection-start]:text-zinc-900 ' +
				'data-[selection-end]:bg-primary data-[selection-end]:text-zinc-900 ' +
				'focus-visible:ring-2 focus-visible:ring-primary/50';
}

/** Wrapper for range-calendar cells (paints the in-range band). */
export const rangeCellWrap =
	'data-[selected]:bg-primary/10 data-[selection-start]:rounded-l-xl data-[selection-end]:rounded-r-xl ' +
	'flex items-center justify-center';

function weekdayClass(theme: DsDateTheme = 'light') {
	return theme === 'dark'
		? 'text-center text-xs font-medium text-zinc-500 py-1 w-8'
		: 'text-center text-xs font-medium text-zinc-400 py-1 w-8';
}

/** Calendar header: prev / month-year heading / next. */
export function CalHeader({ theme = 'light' }: { theme?: DsDateTheme } = {}) {
	const navButtonClass =
		theme === 'dark'
			? 'p-1.5 rounded-xl hover:bg-zinc-700 text-zinc-400 transition-colors outline-none focus-visible:ring-2 focus-visible:ring-primary/50'
			: 'p-1.5 rounded-xl hover:bg-zinc-100 text-zinc-500 transition-colors outline-none focus-visible:ring-2 focus-visible:ring-primary/50';
	const headingClass = theme === 'dark' ? 'text-sm font-semibold text-zinc-100' : 'text-sm font-semibold text-zinc-900';

	return (
		<header className="mb-3 flex items-center justify-between">
			<Button slot="previous" className={navButtonClass}>
				<ChevronLeft size={16} />
			</Button>
			<Heading className={headingClass} />
			<Button slot="next" className={navButtonClass}>
				<ChevronRight size={16} />
			</Button>
		</header>
	);
}

/** Single-selection month grid (for DatePicker / DateTimePicker). */
export function SingleCalendarGrid({ theme = 'light' }: { theme?: DsDateTheme } = {}) {
	return (
		<Calendar>
			<CalHeader theme={theme} />
			<CalendarGrid className="border-collapse">
				<CalendarGridHeader>
					{(day) => <CalendarHeaderCell className={weekdayClass(theme)}>{day}</CalendarHeaderCell>}
				</CalendarGridHeader>
				<CalendarGridBody>
					{(date) => <CalendarCell date={date} className={dayCellClass(theme)} />}
				</CalendarGridBody>
			</CalendarGrid>
		</Calendar>
	);
}

// --- In-popover time selector -------------------------------------------------

/** Minute increment for the in-popover minutes column. Exact minutes stay typeable. */
export const TIME_STEP_MINUTES = 10;

const HOURS = Array.from({ length: 24 }, (_, i) => i);
const MINUTES = Array.from({ length: 60 / TIME_STEP_MINUTES }, (_, i) => i * TIME_STEP_MINUTES);
const pad2 = (n: number) => String(n).padStart(2, '0');

const timeCellClass =
	'w-full px-3 py-1.5 rounded-xl text-sm font-medium tabular-nums text-zinc-700 ' +
	'text-center transition-colors cursor-default outline-none ' +
	'hover:bg-zinc-100 ' +
	'data-[selected]:bg-primary data-[selected]:text-zinc-900 data-[selected]:hover:bg-primary ' +
	'focus-visible:ring-2 focus-visible:ring-primary/50';

/** A single scrollable column of numeric time values (hours or minutes). */
function TimeColumn({
	label,
	values,
	selected,
	onSelect,
}: {
	label: string;
	values: number[];
	selected: number | null;
	onSelect: (n: number) => void;
}) {
	const listRef = useRef<HTMLDivElement>(null);
	const selectedRef = useRef<HTMLButtonElement>(null);

	// Auto-scroll the selected value into view when the popover opens / value changes.
	useLayoutEffect(() => {
		const el = selectedRef.current;
		const list = listRef.current;
		if (el && list) {
			list.scrollTop = el.offsetTop - list.clientHeight / 2 + el.clientHeight / 2;
		}
	}, [selected]);

	return (
		<div className="flex min-w-0 flex-col">
			<span className="mb-1 text-center text-xs font-medium text-zinc-400">{label}</span>
			<div ref={listRef} className="h-[228px] w-16 overflow-y-auto pr-1 [scrollbar-width:thin]">
				<div className="flex flex-col gap-0.5">
					{values.map((n) => {
						const isSelected = n === selected;
						return (
							<button
								key={n}
								ref={isSelected ? selectedRef : undefined}
								type="button"
								data-selected={isSelected || undefined}
								aria-pressed={isSelected}
								className={timeCellClass}
								onClick={() => onSelect(n)}
							>
								{pad2(n)}
							</button>
						);
					})}
				</div>
			</div>
		</div>
	);
}

/**
 * Hours (00–23) + minutes (00,10,…,50) selector, 24-hour. Clicking sets that
 * part on the controlled value via `@internationalized/date` `.set(...)`,
 * preserving the date and the other part. Falls back to today's date when no
 * value is set yet. The typed field segments and these columns drive the same
 * controlled value.
 */
export function TimeSelector({
	value,
	onChange,
	onCommit,
}: {
	value: CalendarDateTime | null;
	onChange: (v: CalendarDateTime) => void;
	/** Called when the minute (final step) is picked — closes the popover. */
	onCommit?: () => void;
}) {
	const base = (): CalendarDateTime => {
		if (value) return value;
		const t = today(getLocalTimeZone());
		return new CalendarDateTime(t.year, t.month, t.day, 0, 0);
	};

	return (
		<div className="flex flex-col">
			<div className="mb-1 flex items-center gap-1.5 text-sm font-semibold text-zinc-900">
				<Clock size={14} className="text-zinc-400" />
				<span>{value ? `${pad2(value.hour)}:${pad2(value.minute)}` : 'Time'}</span>
			</div>
			<div className="flex gap-2">
				<TimeColumn
					label="HH"
					values={HOURS}
					selected={value ? value.hour : null}
					// Hour selection updates the value but must NOT close the popover.
					onSelect={(hour) => onChange(base().set({ hour }))}
				/>
				<TimeColumn
					label="MM"
					values={MINUTES}
					selected={value ? value.minute : null}
					// Minute selection is the final commit step: set the value AND close.
					onSelect={(minute) => {
						onChange(base().set({ minute }));
						onCommit?.();
					}}
				/>
			</div>
		</div>
	);
}

/**
 * Popover body for a date+time picker: the single-month calendar plus the
 * in-popover {@link TimeSelector} beside it. Both the typed field segments and
 * this selector drive the same controlled value.
 */
export function DateTimeCalendarGrid({
	value,
	onChange,
	onCommit,
}: {
	value: CalendarDateTime | null;
	onChange: (v: CalendarDateTime) => void;
	/** Closes the popover; fired when the minute (final step) is selected. */
	onCommit?: () => void;
}) {
	return (
		<div className="flex items-stretch gap-4">
			<SingleCalendarGrid />
			<div className="w-px self-stretch bg-zinc-100" />
			<TimeSelector value={value} onChange={onChange} onCommit={onCommit} />
		</div>
	);
}
